import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { DispatchDatePicker, istDate, shortDateLabel } from "@/components/DispatchDatePicker";
import {
  ArrowLeft,
  ClipboardPaste,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Send,
  Shield,
  X,
  Plus,
  Info,
  Upload,
  FileSpreadsheet,
  Search,
  Download,
  Copy,
  PackageCheck,
} from "lucide-react";
import DeliveredExcelModal from "../components/DeliveredExcelModal";
import { CoolingPanel, coolKey, istDayOf, isNewDac } from "../components/CoolingPanel";
import { MatchedAgenciesHeader } from "../components/MatchedAgenciesHeader";

const SAMPLE = `YASHODHAN HP GAS\t600372\t1\tJitendra\tabc\t9046873734\tPatna@123\tPl share DAC 9960 for delivery
VICKY GAS AGENCY\t7500000071728197\t31\tRajendra\tabc\t9522040905\tPatna@123\tShare DAC 706805 on delivery`;

// Format an ISO timestamp (e.g. "2026-01-15T12:04:33+00:00") into a compact
// IST-friendly `DD-MM-YYYY` string so the Bulk Paste duplicate badge can
// show admins EXACTLY when the previously-dispatched row was sent. Returns
// an empty string if the input is missing/unparseable so callers can guard
// with a truthy check before rendering.
function formatPrevDispatchDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    // Force IST (UTC+5:30) so admins see the same day they saw in chat,
    // regardless of the browser's timezone (some staff use laptops set
    // to UTC / other TZs which would otherwise drift by 5-6 hours).
    return d.toLocaleDateString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function BulkPaste() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [err, setErr] = useState("");
  const [results, setResults] = useState(null);
  // Feb 2026: preview state gets cleared after commit, so the dispatched
  // rows lose the group_id → group_name mapping. Snapshot it BEFORE the
  // clear so the summary can show "PREET INDANE · 7 sent" instead of
  // the cryptic "Group 4d02d918… · 7 sent".
  const [resultsNameMap, setResultsNameMap] = useState({});
  // Aliases saved in the last commit — surfaced under the Dispatched
  // banner so admins can see which pasted labels are now permanently
  // routed. Prevents "same paste unmatches every time" confusion.
  const [aliasesSaved, setAliasesSaved] = useState({ created: [], refreshed: [] });
  const [sendAnyway, setSendAnyway] = useState(() => new Set());
  const [dispatchDate, setDispatchDate] = useState(() => istDate(0));
  const canForce = !!(user?.is_owner || user?.role === "admin");
  const lockDays = [istDate(0), dispatchDate];
  const isCoolDispatchable = (gid, e) =>
    canForce && sendAnyway.has(coolKey(gid, e.lpg_id)) && !lockDays.includes(istDayOf(e.last_sent_at));
  // Agencies unticked in the preview — their rows are hidden and never dispatched.
  const [skippedGroups, setSkippedGroups] = useState(() => new Set());
  const isRouted = (u) => u.assigned_group_id && !skippedGroups.has(u.assigned_group_id);
  const toggleSkipped = (gid) =>
    setSkippedGroups((s) => {
      const n = new Set(s);
      if (n.has(gid)) n.delete(gid);
      else n.add(gid);
      return n;
    });
  const isDispatchable = (gid, e) =>
    !skippedGroups.has(gid) && (e.db_status !== "cooling_duplicate" || isCoolDispatchable(gid, e));
  const toggleSendAnyway = (k) =>
    setSendAnyway((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  // "Mark all as Delivered" toggle (Sep 2026 admin ask):
  // when ticked, every LPG entry dispatched via bulk-paste is
  // auto-flagged delivered so the source portal doesn't need to have a
  // DELIVERED column.
  const [markDelivered, setMarkDelivered] = useState(false);
  // Diagonal-shift guard: when the preview response reports a diagonal
  // shift, the Dispatch button is DISABLED until the admin explicitly
  // ticks "I manually verified the data is correct". This prevents a
  // corrupt paste from silently dispatching a bad ledger.
  const [diagonalConfirmed, setDiagonalConfirmed] = useState(false);
  // Modal state for "Create New Agency" — { idx, agency_label, name }
  const [createAgency, setCreateAgency] = useState(null);
  const [createBusy, setCreateBusy] = useState(false);
  // Banner shown when the backend reused an existing agency instead of
  // creating a new one (duplicate guard).
  const [reuseNotice, setReuseNotice] = useState("");
  // Team-member attribution — persists across pastes via localStorage.
  const [uploaderName, setUploaderName] = useState(
    () => localStorage.getItem("gc_bulk_uploader") || ""
  );
  // File-upload status (filename + row count) shown under the file picker.
  const [fileInfo, setFileInfo] = useState("");
  // Bulk-assign dropdown value — used by the "Apply to all unassigned" bar.
  const [bulkAssignGid, setBulkAssignGid] = useState("");
  // Known uploader names — union of `team_staff` registered `staff_name`s
  // (official, linked to a ledger login) + historical uploaders in the DB.
  // Fetched on mount so the "Uploaded By" combo box offers exact-match picks.
  const [uploaders, setUploaders] = useState({ registered: [], legacy: [] });
  // Iter97: Manage popover state — admin can enable/disable team_staff
  // members so disabled ones vanish from the "Uploaded by" dropdown.
  const [showManageModal, setShowManageModal] = useState(false);
  const [manageMembers, setManageMembers] = useState([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageTogglingId, setManageTogglingId] = useState("");
  // Iter82b: progress of the master "Download All (Excel)" button —
  // shows `Downloading 3/8...` on the button while looping through
  // individual `/messages/{id}/excel` calls. Null when idle.
  const [downloadProgress, setDownloadProgress] = useState(null);

  // Shared helper: fetch one message's Excel from the existing chatbox
  // endpoint and trigger a native download. Filename comes from the
  // `Content-Disposition` header so it stays identical to what the chat
  // UI produces (e.g. `SHEEL_INDANE_GAS_SERVICE_ROHINI_DELHI_2026-09-29_4.xlsx`).
  // Used by BOTH the master "Download All" loop and the per-agency
  // `⬇` button so format + filename never drift.
  const downloadOneAgency = async (messageId, fallbackLabel = "") => {
    const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const tok = localStorage.getItem("gc_token") || "";
    const resp = await fetch(`${backend}/api/messages/${messageId}/excel`, {
      headers: { Authorization: `Bearer ${tok}` },
    });
    if (!resp.ok) {
      const err = await resp.text();
      throw new Error(err || `HTTP ${resp.status}`);
    }
    const blob = await resp.blob();
    const cd = resp.headers.get("Content-Disposition") || "";
    const match = cd.match(/filename="?([^";]+)"?/);
    const filename = match
      ? match[1]
      : `${(fallbackLabel || "Dispatch").replace(/\s+/g, "_")}_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const fileInputRef = useRef(null);
  // Delivered-Excel import (Feb 2026): parallel upload path that produces
  // date-wise GREEN chat cards instead of routing rows through the
  // regular pending → DAC → deliver flow. Separate ref so the two file
  // inputs never collide, and a `deliveredFile` state that a dedicated
  // modal consumes to run its own preview → commit dance.
  const deliveredFileRef = useRef(null);
  const [deliveredFile, setDeliveredFile] = useState(null);
  // Parser trace state (see doTrace below).
  const [trace, setTrace] = useState(null);
  const [tracing, setTracing] = useState(false);

  useEffect(() => {
    localStorage.setItem("gc_bulk_uploader", uploaderName);
  }, [uploaderName]);

  useEffect(() => {
    api
      .get("/uploaders")
      .then(({ data }) => {
        const reg = data.registered || [];
        setUploaders({ registered: reg, legacy: data.legacy || [] });
        // Purge stale localStorage picks (legacy free-text like "AS",
        // "KO", "Ravi QA Test 1783487608") so the strict dropdown starts
        // clean. Only survive if the previous pick matches a currently
        // registered team_staff.
        setUploaderName((prev) =>
          prev && reg.includes(prev) ? prev : ""
        );
      })
      .catch(() => {}); // best-effort; dropdown will still render
  }, []);

  // Iter97: load all team members (incl. disabled) when the Manage
  // popover opens. Separate call so the dropdown fetch stays lean.
  const loadManageMembers = async () => {
    setManageLoading(true);
    try {
      const { data } = await api.get("/uploaders?include_disabled=true");
      setManageMembers(data.members || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setManageLoading(false);
    }
  };

  useEffect(() => {
    if (showManageModal) {
      loadManageMembers();
    }
  }, [showManageModal]);

  const toggleMemberEnabled = async (m) => {
    const next = !m.enabled;
    setManageTogglingId(m.id);
    try {
      await api.patch(`/uploaders/${m.id}/enabled`, { enabled: next });
      // Optimistic local update
      setManageMembers((prev) =>
        prev.map((x) => (x.id === m.id ? { ...x, enabled: next } : x)),
      );
      toast.success(`${m.staff_name} ${next ? "enabled" : "disabled"}`);
      // Refresh dropdown source so disabled vanishes immediately
      try {
        const { data } = await api.get("/uploaders");
        const reg = data.registered || [];
        setUploaders({ registered: reg, legacy: data.legacy || [] });
        // Clear current selection if it was just disabled
        if (!reg.includes(uploaderName)) {
          setUploaderName("");
        }
      } catch {}
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setManageTogglingId("");
    }
  };

  // Detect if the first non-empty row looks like a HEADER row and map
  // its columns to canonical fields (agency / consumer / dac / mobile).
  // Portal exports we normalise on-the-fly:
  //   • IOCL bookings-history: When, Relationship ID, Status, Mobile,
  //     Sales Order, DAC, Invoice URL, Agency, Message
  //   • Bharat bookings-history: When, Consumer No, Mobile, Agency,
  //     Status, Order No, Cash Memo, DAC, DAC Status, Message
  //   • HPCL exports: When, Consumer No, Mobile, Agency, DAC, …
  // Returns `null` if the row does not look like a header (any column
  // is purely numeric or all cells are empty) — in that case the caller
  // falls back to the raw tab-join behaviour.
  //
  // Header matches are FUZZY (case-insensitive substring) so future
  // column renames (`DAC Code`, `Delivery Auth Code`, `LPG ID`, etc.)
  // do not silently break the mapping.
  const detectHeader = (row) => {
    if (!Array.isArray(row) || row.length < 3) return null;
    const cells = row.map((c) => String(c ?? "").trim());
    // A real header row has NO purely numeric cell (headers are labels).
    if (cells.some((c) => c && /^\d{4,}$/.test(c))) return null;
    if (cells.every((c) => !c)) return null;
    const norm = cells.map((c) => c.toLowerCase().replace(/[^a-z0-9]/g, ""));
    const idx = { agency: -1, consumer: -1, dac: -1, mobile: -1 };
    norm.forEach((n, i) => {
      if (n && idx.agency === -1 &&
          (n.includes("agency") || n.includes("distributor") ||
           n.includes("dealer") || n === "distributorname")) {
        idx.agency = i;
      }
      if (n && idx.consumer === -1 &&
          (n === "consumerno" || n === "consumernumber" ||
           n === "consumerid" || n === "lpgid" || n === "lpgno" ||
           n === "relationshipid" || n.includes("consumer") ||
           n === "lpgconsumerno" || n === "consumer")) {
        idx.consumer = i;
      }
      if (n && idx.dac === -1 &&
          (n === "dac" || n === "daccode" ||
           n === "deliveryauthcode" || n === "deliveryauthenticationcode" ||
           (n.includes("dac") && !n.includes("status")) ||
           (n.includes("delivery") && n.includes("code")))) {
        idx.dac = i;
      }
      if (n && idx.mobile === -1 &&
          (n === "mobile" || n === "mobileno" || n === "phone" ||
           n === "phoneno" || n.includes("mobile"))) {
        idx.mobile = i;
      }
    });
    // Need at least Consumer + DAC to be a usable portal header.
    if (idx.consumer === -1 || idx.dac === -1) return null;
    return idx;
  };

  // Parse a .csv / .xlsx / .xls file client-side. Every sheet's every row
  // is flattened to a TAB-joined line; the existing backend parser then
  // extracts the LPG-ID + DAC pairs using the same rules.
  //
  // When a recognisable portal header row is detected (Consumer No, DAC,
  // Agency, Mobile…), each subsequent row is re-emitted in the CANONICAL
  // order `AGENCY \t CONSUMER \t DAC \t MOBILE` — 4 cells only. This
  // makes future portal-export layout tweaks (new columns, reordered
  // columns, renamed columns) a non-event for the backend parser: it
  // always sees the same simple 4-cell shape.
  const handleFile = async (file) => {
    if (!file) return;
    setErr("");
    setFileInfo("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", raw: false });
      const lines = [];
      let normalisedFromHeader = 0;
      let rawRows = 0;
      for (const sheetName of wb.SheetNames) {
        const ws = wb.Sheets[sheetName];
        if (!ws) continue;
        // sheet_to_json with header:1 → rows as arrays. defval:"" keeps
        // empty cells so the column alignment is preserved.
        const rows = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          defval: "",
          blankrows: false,
          raw: false,
        });
        // Try header detection on first non-empty row.
        let headerIdx = null;
        let headerRow = -1;
        for (let r = 0; r < Math.min(5, rows.length); r++) {
          const row = rows[r];
          if (!Array.isArray(row)) continue;
          if (row.every((c) => !String(c ?? "").trim())) continue;
          headerIdx = detectHeader(row);
          if (headerIdx) {
            headerRow = r;
            break;
          }
          break; // first non-empty row wasn't a header → don't scan further
        }
        for (let r = 0; r < rows.length; r++) {
          if (r === headerRow) continue; // skip the header row itself
          const row = rows[r];
          if (!Array.isArray(row)) continue;
          const cells = row.map((c) => String(c ?? "").trim());
          if (cells.every((c) => !c)) continue;
          rawRows++;
          if (headerIdx) {
            // Emit canonical 4-cell shape. Missing cells → empty string.
            const agency = headerIdx.agency >= 0 ? cells[headerIdx.agency] || "" : "";
            const consumer = headerIdx.consumer >= 0 ? cells[headerIdx.consumer] || "" : "";
            const dac = headerIdx.dac >= 0 ? cells[headerIdx.dac] || "" : "";
            const mobile = headerIdx.mobile >= 0 ? cells[headerIdx.mobile] || "" : "";
            // Skip rows with no Consumer AND no DAC — they carry no
            // useful payload (e.g. summary / totals rows some portals
            // append at the bottom of the sheet).
            if (!consumer && !dac) continue;
            lines.push([agency, consumer, dac, mobile].join("\t"));
            normalisedFromHeader++;
          } else {
            // No header detected — preserve the original raw shape so
            // the backend's positional heuristics have a chance.
            lines.push(cells.join("\t"));
          }
        }
      }
      const joined = lines.join("\n");
      setText((prev) => (prev ? prev + "\n" + joined : joined));
      if (normalisedFromHeader > 0) {
        setFileInfo(
          `${file.name} · ${lines.length} row(s) loaded · ` +
          `${normalisedFromHeader} auto-mapped from portal headers`
        );
      } else {
        setFileInfo(`${file.name} · ${lines.length} row(s) loaded`);
      }
    } catch (e) {
      setErr(`Could not read file: ${e.message || e}`);
    }
  };

  // Privacy guard
  if (user && user.role !== "admin" && !user.auto_join_groups) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0] p-6">
        <div className="max-w-md text-center">
          <Shield className="w-12 h-12 mx-auto text-[#8A6046] mb-3" />
          <h1 className="text-xl font-semibold text-[#1C1C19]">Restricted</h1>
          <p className="text-sm text-[#6B6B61] mt-2">
            Bulk paste is available only to admins and Selected reviewers.
          </p>
          <button
            onClick={() => navigate("/home")}
            className="mt-4 px-4 py-2 bg-[#385C4B] text-white rounded-md text-sm"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const doPreview = async () => {
    if (!text.trim()) return;
    // Strict validation — a registered team_staff must be picked so
    // dispatch attribution is guaranteed to be 100% correct.
    if (!uploaderName || !uploaders.registered.includes(uploaderName)) {
      setErr(
        uploaders.registered.length
          ? "Pehle 'Uploaded by (team member)' dropdown se registered user select karo."
          : "Koi registered team member nahi hai. Admin → Users me role=team_staff wala user create karo pehle."
      );
      return;
    }
    setLoading(true);
    setErr("");
    setResults(null);
    setAliasesSaved({ created: [], refreshed: [] });
    try {
      const { data } = await api.post("/bulk-paste/preview", { text });
      // Initialise an `assigned_group_id` slot on each unmatched row so the
      // user can route it manually via a per-row dropdown.
      setPreview({
        ...data,
        unmatched: (data.unmatched || []).map((u) => ({
          ...u,
          assigned_group_id: "",
        })),
      });
      const init = new Set();
      (data.groups || []).forEach((g) =>
        (g.entries || []).forEach((e) => {
          if (e.db_status === "cooling_duplicate" && isNewDac(e)) init.add(coolKey(g.group_id, e.lpg_id));
        })
      );
      setSendAnyway(init);
      setSkippedGroups(new Set());
      // Reset the diagonal-confirm checkbox on every new preview so a
      // fresh corrupt paste starts BLOCKED even if the previous paste
      // was already confirmed.
      setDiagonalConfirmed(false);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Trace mode — asks the backend to run the parser step-by-step on the
  // first ~20 rows and returns which regex passes fired, what tokens each
  // pass captured, and which pass produced the final accepted DAC.
  // Used to debug portal-CSV format bugs live (e.g. "why is DAC 00399
  // showing up when the real DAC is 717090?").
  const doTrace = async () => {
    if (!text.trim()) return;
    setTracing(true);
    setErr("");
    try {
      const { data } = await api.post("/bulk-paste/trace", { text });
      setTrace(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setTracing(false);
    }
  };

  const setUnmatchedGroup = (idx, gid) => {
    setPreview((p) => {
      const next = { ...p, unmatched: [...p.unmatched] };
      const targetLabel = next.unmatched[idx]?.agency_label;
      // Bulk-apply: when the user picks a group for one row, every other
      // row with the SAME agency_label gets the same group. Saves clicking
      // 50+ identical "Rc Gramin vitrak" rows individually.
      next.unmatched = next.unmatched.map((u, i) =>
        i === idx || (gid && u.agency_label === targetLabel && !u.assigned_group_id)
          ? { ...u, assigned_group_id: gid }
          : u
      );
      return next;
    });
  };

  // "Apply to all unassigned" — single-click bulk-assign every unmatched row
  // (that hasn't been routed yet) to a single group. Different agency_label
  // variants that actually belong to the same distributor can all be routed
  // in one shot without clicking each dropdown.
  const applyBulkAssign = () => {
    if (!bulkAssignGid) return;
    setPreview((p) => ({
      ...p,
      unmatched: p.unmatched.map((u) =>
        u.assigned_group_id ? u : { ...u, assigned_group_id: bulkAssignGid }
      ),
    }));
  };

  // "Apply all suggestions" — one-click routes every unassigned row that
  // has a fuzzy suggestion to that suggested group. When agencies are
  // mixed (e.g. YOGIKRUPA + SHIV SHAKTI + RC INDANE in a single paste),
  // each row lands in its OWN suggested bucket — no need to click per
  // agency. Rows without a suggestion stay unassigned.
  const applyAllSuggestions = () => {
    setPreview((p) => ({
      ...p,
      unmatched: p.unmatched.map((u) =>
        u.assigned_group_id || !u.suggested_group_id
          ? u
          : { ...u, assigned_group_id: u.suggested_group_id }
      ),
    }));
  };

  // "Apply all for THIS agency" — routes every row sharing `agencyLabel`
  // (case-insensitive) to `gid`. Used by the per-agency mini-panel so
  // when 40 rows come from "R.C. INDANE" with the same spelling, one
  // click assigns them all — without touching rows from other agencies.
  const applyAllForAgency = (agencyLabel, gid) => {
    if (!gid) return;
    const target = String(agencyLabel || "").trim().toLowerCase();
    setPreview((p) => ({
      ...p,
      unmatched: p.unmatched.map((u) =>
        !u.assigned_group_id &&
        String(u.agency_label || "").trim().toLowerCase() === target
          ? { ...u, assigned_group_id: gid }
          : u
      ),
    }));
  };

  const removeUnmatched = (idx) => {
    setPreview((p) => ({
      ...p,
      unmatched: p.unmatched.filter((_, i) => i !== idx),
    }));
  };

  // Generate an XLSX report of every duplicate row detected in this preview
  // (in-paste dupes AND already-sent cooling matches) with:
  //   - red-highlight on in-paste dupes (rows Excel repeated)
  //   - amber-highlight on already-sent (DB cooling matches)
  //   - a Summary section at top with counts
  //   - previous dispatch date + uploader + earlier DAC for every DB match
  // File name: `bulk-paste-duplicates-YYYYMMDD-HHMM.xlsx`
  const downloadDuplicateReport = async () => {
    if (!preview) return;
    try {
      const ExcelJSMod = await import("exceljs");
      const ExcelJS = ExcelJSMod.default || ExcelJSMod;
      const wb = new ExcelJS.Workbook();
      wb.creator = "XYZ Chatting · Bulk Paste";
      wb.created = new Date();
      const ws = wb.addWorksheet("Duplicate Report", {
        views: [{ state: "frozen", ySplit: 8 }],
      });

      // ---- Summary block (rows 1-6) ---------------------------------
      const inPasteDupes = preview.dropped_in_paste || [];
      const coolingRows = [];
      for (const g of preview.groups || []) {
        for (const e of g.entries || []) {
          if (e.db_status === "cooling_duplicate" || e.db_status === "wrong_will_flip") {
            coolingRows.push({ ...e, group_name: g.group_name });
          }
        }
      }
      const totalDupes = inPasteDupes.length + coolingRows.length;

      const titleRow = ws.addRow(["BULK PASTE · DUPLICATE REPORT"]);
      titleRow.font = { bold: true, size: 14, color: { argb: "FF385C4B" } };
      ws.mergeCells(`A${titleRow.number}:G${titleRow.number}`);

      const now = new Date();
      ws.addRow([`Generated: ${now.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST`]);
      ws.addRow([`Total duplicate rows: ${totalDupes}`]);
      const rIn = ws.addRow([`  • In-paste duplicates (same row 2x in Excel): ${inPasteDupes.length}`]);
      rIn.font = { color: { argb: "FF9A5B15" } };
      const rDb = ws.addRow([`  • Already-sent (DB match within ${preview.cooling_days}-day cooling window): ${coolingRows.length}`]);
      rDb.font = { color: { argb: "FF64748B" } };
      ws.addRow([]);

      // ---- Header row (row 7) ---------------------------------------
      const headerRow = ws.addRow([
        "Type",
        "LPG / Consumer ID",
        "DAC",
        "Agency",
        "Previously sent on",
        "Previously by (staff)",
        "Earlier DAC",
      ]);
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF385C4B" },
        };
        cell.alignment = { vertical: "middle", horizontal: "left" };
      });

      const fmtDate = (iso) => {
        if (!iso) return "";
        try {
          const d = new Date(iso);
          if (Number.isNaN(d.getTime())) return "";
          return d.toLocaleDateString("en-GB", {
            timeZone: "Asia/Kolkata",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        } catch {
          return "";
        }
      };

      // ---- In-paste dupes (amber-red highlight) ---------------------
      for (const d of inPasteDupes) {
        const row = ws.addRow([
          "IN-PASTE DUPLICATE",
          String(d.lpg_id || ""),
          String(d.dac_code || ""),
          String(d.agency_label || ""),
          "",
          "",
          "",
        ]);
        row.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFDE68A" }, // amber-200
          };
          cell.font = { color: { argb: "FF7C2D12" }, bold: false };
        });
      }

      // ---- DB cooling / wrong_will_flip dupes (red highlight) -------
      for (const e of coolingRows) {
        const typeLabel =
          e.db_status === "wrong_will_flip"
            ? "WILL AUTO-RESENT (complaint)"
            : `ALREADY-SENT (${preview.cooling_days}d cooling)`;
        const row = ws.addRow([
          typeLabel,
          String(e.lpg_id || ""),
          String(e.dac_code || ""),
          String(e.group_name || ""),
          fmtDate(e.last_sent_at),
          String(e.last_sent_uploader || ""),
          String(e.last_sent_dac || ""),
        ]);
        const isFlip = e.db_status === "wrong_will_flip";
        row.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: isFlip ? "FFBFDBFE" : "FFFECACA" }, // blue-200 vs red-200
          };
          cell.font = {
            color: { argb: isFlip ? "FF1E3A8A" : "FF7F1D1D" },
          };
        });
      }

      if (totalDupes === 0) {
        const noneRow = ws.addRow(["No duplicates detected in this paste."]);
        noneRow.font = { italic: true, color: { argb: "FF6B6B61" } };
        ws.mergeCells(`A${noneRow.number}:G${noneRow.number}`);
      }

      // Auto-width columns based on content
      ws.columns.forEach((col) => {
        let max = 12;
        col.eachCell({ includeEmpty: false }, (cell) => {
          const l = String(cell.value ?? "").length;
          if (l > max) max = l;
        });
        col.width = Math.min(max + 2, 46);
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = now
        .toISOString()
        .replace(/[-:]/g, "")
        .replace(/T/, "-")
        .slice(0, 13);
      a.href = url;
      a.download = `bulk-paste-duplicates-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Duplicate report downloaded (${totalDupes} rows highlighted)`);
    } catch (err) {
      console.error("downloadDuplicateReport failed", err);
      toast.error("Report generate nahi ho paya: " + (err?.message || String(err)));
    }
  };

  // Copy a WhatsApp-friendly plain-text summary of every duplicate row
  // detected in this preview to the clipboard so admins can paste it
  // directly into a staff WhatsApp group / chat and ask for a recheck.
  // Format (with emojis for chat readability):
  //   🔁 Duplicate Report · DD-MM-YYYY IST
  //   Total: N dupes  (X in-paste · Y already-sent)
  //   📋 In-paste duplicates:
  //   • AGENCY · LPG · DAC XXXX
  //   🔁 Already-sent:
  //   • AGENCY · LPG · DAC XXXX
  //     ↳ previously sent DD-MM-YYYY by STAFF · earlier DAC ZZZZ
  const copyDuplicateWhatsAppText = async () => {
    if (!preview) return;
    const inPasteDupes = preview.dropped_in_paste || [];
    const coolingRows = [];
    const flipRows = [];
    for (const g of preview.groups || []) {
      for (const e of g.entries || []) {
        if (e.db_status === "cooling_duplicate") {
          coolingRows.push({ ...e, group_name: g.group_name });
        } else if (e.db_status === "wrong_will_flip") {
          flipRows.push({ ...e, group_name: g.group_name });
        }
      }
    }
    const total = inPasteDupes.length + coolingRows.length + flipRows.length;
    if (total === 0) {
      toast.info("Koi duplicate nahi hai — copy karne ke liye kuch bhi nahi");
      return;
    }
    const fmtDate = (iso) => {
      if (!iso) return "";
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        return d.toLocaleDateString("en-GB", {
          timeZone: "Asia/Kolkata",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      } catch {
        return "";
      }
    };
    const todayIst = new Date().toLocaleDateString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const lines = [];
    lines.push(`🔁 *Duplicate Report* · ${todayIst} IST`);
    lines.push(
      `Total: *${total}* dupes  (` +
        [
          inPasteDupes.length ? `${inPasteDupes.length} in-paste` : null,
          coolingRows.length ? `${coolingRows.length} already-sent` : null,
          flipRows.length ? `${flipRows.length} auto-resent` : null,
        ]
          .filter(Boolean)
          .join(" · ") +
        `)`
    );
    if (inPasteDupes.length) {
      lines.push("");
      lines.push(`📋 *In-paste duplicates* (${inPasteDupes.length}):`);
      for (const d of inPasteDupes) {
        lines.push(
          `• ${d.agency_label || "—"} · ${d.lpg_id} · DAC ${d.dac_code || "—"}`
        );
      }
    }
    if (coolingRows.length) {
      lines.push("");
      lines.push(`🔁 *Already-sent (${preview.cooling_days}d cooling)* (${coolingRows.length}):`);
      for (const e of coolingRows) {
        lines.push(
          `• ${e.group_name} · ${e.lpg_id} · DAC ${e.dac_code}`
        );
        const prevDate = fmtDate(e.last_sent_at);
        const prevBy = (e.last_sent_uploader || "").trim();
        const prevDac = (e.last_sent_dac || "").trim();
        if (prevDate) {
          const tail = [
            `previously sent ${prevDate}`,
            prevBy ? `by ${prevBy}` : null,
            prevDac && prevDac !== e.dac_code ? `earlier DAC ${prevDac}` : null,
          ]
            .filter(Boolean)
            .join(" · ");
          lines.push(`   ↳ ${tail}`);
        }
      }
    }
    if (flipRows.length) {
      lines.push("");
      lines.push(`↻ *Auto-resent (complaint → will flip on dispatch)* (${flipRows.length}):`);
      for (const e of flipRows) {
        lines.push(
          `• ${e.group_name} · ${e.lpg_id} · DAC ${e.dac_code}`
        );
        const prevDate = fmtDate(e.last_sent_at);
        if (prevDate) {
          const prevBy = (e.last_sent_uploader || "").trim();
          const tail = [
            `earlier complaint on ${prevDate}`,
            prevBy ? `by ${prevBy}` : null,
          ]
            .filter(Boolean)
            .join(" · ");
          lines.push(`   ↳ ${tail}`);
        }
      }
    }
    const text = lines.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`WhatsApp text copied (${total} duplicates)`);
    } catch (err) {
      // Older browsers / insecure contexts — fall back to a hidden textarea
      // + document.execCommand("copy") so the user still gets the text.
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success(`WhatsApp text copied (${total} duplicates)`);
      } catch (err2) {
        console.error("copyDuplicateWhatsAppText failed", err, err2);
        toast.error("Copy nahi ho paya: " + (err2?.message || String(err2)));
      }
    }
  };

  // Dispatch count = fresh rows + admin-ticked "Send anyway" cooling rows.
  const dispatchTotals = () => {
    const routed = preview.unmatched.filter(isRouted).length;
    return {
      totalIds: preview.groups.reduce((sum, g) => sum + (g.entries || []).filter((e) => isDispatchable(g.group_id, e)).length, 0) + routed,
      totalGroups: preview.groups.filter((g) => (g.entries || []).some((e) => isDispatchable(g.group_id, e))).length + routed,
    };
  };
  const backdated = dispatchDate !== istDate(0);

  const commit = async () => {
    if (!preview) return;
    if (backdated && !window.confirm(`Ye ${dispatchTotals().totalIds} IDs ${shortDateLabel(dispatchDate)} ki date mein jayengi. Pakka?`)) return;
    // Build per-group buckets, merging manually-routed unmatched rows in.
    // ALL entries (including `wrong_will_flip` complaint IDs) are sent to
    // the backend. Complaint IDs auto-flip to `resent` status on dispatch.
    // Admins clear complaint tracking MANUALLY via Chat/Complaint Box — the
    // dispatch itself must never silently drop rows the admin pasted.
    const bucketMap = new Map();
    for (const g of preview.groups) {
      const rows = g.entries.filter((e) => isDispatchable(g.group_id, e));
      bucketMap.set(g.group_id, {
        group_id: g.group_id,
        entries: rows.map((e) => ({
          lpg_id: e.lpg_id,
          dac_code: e.dac_code,
          ...(e.mobile ? { mobile: e.mobile } : {}),
        })),
        aliases: [],
        force_lpg_ids: rows.filter((e) => e.db_status === "cooling_duplicate").map((e) => e.lpg_id),
      });
    }
    const manuallyRouted = preview.unmatched.filter(isRouted);
    for (const u of manuallyRouted) {
      let b = bucketMap.get(u.assigned_group_id);
      if (!b) {
        b = {
          group_id: u.assigned_group_id,
          entries: [],
          aliases: [],
          force_lpg_ids: [],
        };
        bucketMap.set(u.assigned_group_id, b);
      }
      b.entries.push({
        lpg_id: u.lpg_id,
        dac_code: u.dac_code,
        ...(u.mobile ? { mobile: u.mobile } : {}),
      });
      if (u.db_status === "cooling_duplicate") b.force_lpg_ids.push(u.lpg_id);
      if (u.agency_label && !b.aliases.includes(u.agency_label)) {
        b.aliases.push(u.agency_label);
      }
    }
    const buckets = Array.from(bucketMap.values()).filter((b) => b.entries.length > 0);
    if (buckets.length === 0) {
      setErr("Nothing to send — assign or add at least one entry first.");
      return;
    }
    setCommitting(true);
    setErr("");
    try {
      const { data } = await api.post("/bulk-paste/commit", {
        groups: buckets,
        mark_delivered: markDelivered,
        uploader_name: uploaderName.trim() || undefined,
        dispatch_date: backdated ? dispatchDate : undefined,
      });
      setResults(data.results || []);
      // Snapshot the group_id → group_name lookup before we clear
      // `preview` below — otherwise the Dispatched summary would lose
      // agency names and fall back to the cryptic 8-char UUID prefix.
      const snap = {};
      (preview?.groups || []).forEach((g) => {
        if (g.group_id) snap[g.group_id] = g.group_name;
      });
      (preview?.available_groups || []).forEach((g) => {
        if (g.id && !snap[g.id]) snap[g.id] = g.name;
      });
      setResultsNameMap(snap);
      const created = data.aliases_created || [];
      const refreshed = data.aliases_refreshed || [];
      setAliasesSaved({ created, refreshed });
      // Show a persistent notice + toast when new aliases were saved so
      // the user knows the same paste will NOT land in unmatched again.
      // This makes the invisible "save-on-commit" behaviour visible.
      if (created.length > 0 || refreshed.length > 0) {
        const parts = [];
        if (created.length > 0) {
          parts.push(
            `${created.length} new agency ${created.length === 1 ? "alias" : "aliases"} saved`
          );
        }
        if (refreshed.length > 0) {
          parts.push(
            `${refreshed.length} re-routed`
          );
        }
        toast.success(
          `${parts.join(" · ")} — next paste with the same label${created.length + refreshed.length === 1 ? "" : "s"} auto-matches ✓`,
          { duration: 6000 }
        );
      }
      setText("");
      setPreview(null);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setCommitting(false);
    }
  };

  const submitCreateAgency = async () => {
    if (!createAgency || !createAgency.name.trim()) return;
    const name = createAgency.name.trim();
    const label = createAgency.agency_label;
    setCreateBusy(true);
    setErr("");
    setReuseNotice("");
    try {
      const { data } = await api.post("/bulk-paste/create-agency", {
        agency_name: name,
        aliases: label ? [label] : [],
      });
      // Reflect new/reused group into the preview state so all rows with
      // the same agency_label auto-assign to it instantly.
      setPreview((p) => {
        if (!p) return p;
        const exists = p.available_groups.some((g) => g.id === data.id);
        const available_groups = exists
          ? p.available_groups
          : [...p.available_groups, { id: data.id, name: data.name }];
        const unmatched = p.unmatched.map((u) =>
          u.agency_label === label
            ? { ...u, assigned_group_id: data.id }
            : u
        );
        return { ...p, available_groups, unmatched };
      });
      if (data.reused) {
        setReuseNotice(
          `"${label}" already exists as "${data.name}" — alias saved, rows auto-assigned.`
        );
      } else {
        setReuseNotice(
          `New agency "${data.name}" created (login password: Patna@123) — rows auto-assigned.`
        );
      }
      setCreateAgency(null);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setCreateBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <header className="bg-white border-b border-[#DCDCD3] px-6 py-3 flex items-center gap-3 sticky top-0 z-20">
        <button
          onClick={() => navigate("/home")}
          className="text-[#6B6B61] hover:text-[#1C1C19]"
          data-testid="bulk-back-button"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2">
          <ClipboardPaste className="w-5 h-5 text-[#385C4B]" />
          <h1
            className="text-lg font-semibold text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Bulk Paste · Multi-Agency Dispatcher
          </h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-5">
        <div className="bg-white border border-[#DCDCD3] rounded-lg p-4">
          <div className="flex flex-wrap items-end gap-3 mb-3 pb-3 border-b border-[#DCDCD3]">
            <div className="flex-1 min-w-[200px]">
              <label
                className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] block mb-1"
                htmlFor="bulk-uploader-name"
              >
                Uploaded by (team member)
              </label>
              {/* STRICT dropdown — only registered team_staff users allowed.
                  Free-text + legacy names removed per user directive
                  (Feb 2026): "REGISTERED USER HAI...WAHI SELECT HO K
                  DISPATCH HONA CHAHIYE.....SUGGESION AA RHA HAI NICHE
                  ESKO HATA DO". Guarantees the "Uploaded By" attribution
                  always maps to a real ledger login. */}
              <div className="flex items-center gap-2">
                <select
                  id="bulk-uploader-name"
                  value={
                    uploaders.registered.includes(uploaderName)
                      ? uploaderName
                      : ""
                  }
                  onChange={(e) => setUploaderName(e.target.value)}
                  data-testid="bulk-uploader-name-input"
                  className="flex-1 px-3 py-2 border border-[#DCDCD3] rounded text-sm focus:outline-none focus:border-[#385C4B] bg-white"
                >
                  <option value="">
                    {uploaders.registered.length
                      ? "— Select a registered team member —"
                      : "No registered team member yet"}
                  </option>
                  {uploaders.registered.map((n) => (
                    <option key={`r-${n}`} value={n}>
                      {`${n} · Registered · linked to ledger login`}
                    </option>
                  ))}
                </select>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setShowManageModal(true)}
                    data-testid="bulk-uploader-manage-btn"
                    className="px-3 py-2 text-xs font-semibold border border-[#DCDCD3] rounded hover:bg-[#F7F6EE] whitespace-nowrap flex items-center gap-1"
                    title="Enable/disable team members from the dropdown"
                  >
                    ⚙ Manage
                  </button>
                )}
              </div>
              <p className="text-[10px] text-[#6B6B61] mt-1">
                {uploaders.registered.length > 0 ? (
                  <>
                    <span className="text-[#385C4B] font-semibold">Tip:</span>{" "}
                    Sirf registered team members hi dispatch karenge. Selected user ke{" "}
                    <b>My Ledger</b> portal me apna upload dikhega — 100% correct
                    attribution.
                  </>
                ) : (
                  <>
                    <span className="text-amber-700 font-semibold">
                      Note:
                    </span>{" "}
                    Koi registered team member nahi hai. Admin → Users me{" "}
                    <b>role = team_staff</b> user create karo pehle.
                  </>
                )}
              </p>
            </div>
            <div className="flex-shrink-0">
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] block mb-1">
                Import file
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  handleFile(e.target.files?.[0]);
                  e.target.value = ""; // allow re-selecting the same file
                }}
                className="hidden"
                data-testid="bulk-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="bulk-file-button"
                className="px-3 py-2 text-sm border border-[#DCDCD3] rounded hover:bg-[#F5F5F0] flex items-center gap-2"
              >
                <FileSpreadsheet className="w-4 h-4 text-[#385C4B]" />
                CSV / Excel…
              </button>
              {/* Delivered-Excel import — separate flow. Same-row Excel
                  (Relationship ID + DAC + Delivered Date) posts date-wise
                  GREEN cards, skipping DAC-dispatch step entirely. */}
              <input
                ref={deliveredFileRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setDeliveredFile(f);
                  e.target.value = "";
                }}
                className="hidden"
                data-testid="delivered-file-input"
              />
              <button
                type="button"
                onClick={() => deliveredFileRef.current?.click()}
                data-testid="delivered-file-button"
                title="Import a delivered-rows CSV or Excel (date-wise green cards, failed / pending rows auto-skipped)"
                className="ml-2 px-3 py-2 text-sm border border-[#385C4B] bg-[#F1F7F3] text-[#385C4B] rounded hover:bg-[#E5F0EA] flex items-center gap-2"
              >
                <PackageCheck className="w-4 h-4" />
                Delivered CSV / Excel…
              </button>
              {fileInfo && (
                <p
                  className="text-[10px] text-[#385C4B] mt-1 font-mono"
                  data-testid="bulk-file-info"
                >
                  {fileInfo}
                </p>
              )}
            </div>
          </div>
          <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">
            Paste Excel / portal dump below <span className="text-[#385C4B]">— or use the file picker above / drag &amp; drop a CSV here</span>
          </label>
          <div
            onDragOver={(e) => {
              // Only handle file drags — text drags into the textarea should
              // remain native so admin can also drop text snippets.
              if (e.dataTransfer?.types?.includes("Files")) {
                e.preventDefault();
                e.currentTarget.classList.add(
                  "ring-2",
                  "ring-[#385C4B]",
                  "ring-offset-2"
                );
              }
            }}
            onDragLeave={(e) =>
              e.currentTarget.classList.remove(
                "ring-2",
                "ring-[#385C4B]",
                "ring-offset-2"
              )
            }
            onDrop={(e) => {
              const files = e.dataTransfer?.files;
              if (files && files.length > 0) {
                e.preventDefault();
                e.currentTarget.classList.remove(
                  "ring-2",
                  "ring-[#385C4B]",
                  "ring-offset-2"
                );
                handleFile(files[0]);
              }
            }}
            className="mt-2 rounded transition-shadow"
            data-testid="bulk-paste-dropzone"
          >
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={SAMPLE}
              data-testid="bulk-paste-textarea"
              spellCheck={false}
              className="w-full h-56 px-3 py-2 text-xs font-mono bg-[#F5F5F0] border border-[#DCDCD3] rounded resize-y focus:outline-none focus:border-[#385C4B]"
            />
          </div>
          <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
            <p className="text-xs text-[#6B6B61]">
              Lines are auto-detected. Mobile / SMS / password text is discarded;
              only <b>LPG ID + DAC code</b> survive. Same row pasted twice → "Duplicate
              auto-removed".
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={doTrace}
                disabled={tracing || !text.trim()}
                data-testid="bulk-trace-button"
                title="Parser trace — dry-run debug view (does NOT dispatch)"
                className="px-3 py-2 border border-[#385C4B] text-[#385C4B] text-sm rounded hover:bg-[#385C4B]/10 disabled:opacity-50 flex items-center gap-2"
              >
                {tracing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                Trace
              </button>
              <button
                onClick={doPreview}
                disabled={loading || !text.trim()}
                data-testid="bulk-preview-button"
                className="px-4 py-2 bg-[#385C4B] text-white text-sm rounded hover:bg-[#2D4A3C] disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ClipboardPaste className="w-4 h-4" />
                )}
                Preview
              </button>
            </div>
          </div>
        </div>

        {trace && (
          <div
            className="bg-white border border-[#DCDCD3] rounded-md p-4"
            data-testid="bulk-trace-panel"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-[#1C1C19] flex items-center gap-2">
                <Search className="w-4 h-4 text-[#385C4B]" />
                Parser trace · first {trace.rows?.length || 0} row(s)
                {trace.truncated && (
                  <span className="text-[10px] font-mono text-[#8A6046]">
                    · truncated (paste has &gt; 20 rows)
                  </span>
                )}
              </div>
              <button
                onClick={() => setTrace(null)}
                data-testid="bulk-trace-close"
                className="text-[#6B6B61] hover:text-[#1C1C19]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              {(trace.rows || []).map((row) => (
                <div
                  key={row.line_no}
                  className="border border-[#DCDCD3] rounded p-3 text-xs"
                  data-testid={`bulk-trace-row-${row.line_no}`}
                >
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <div className="font-mono text-[10px] text-[#6B6B61] tracking-wide">
                      ROW {row.line_no}
                    </div>
                    <div className="text-[11px] text-[#385C4B] truncate">
                      Agency: <b>{row.agency_label || "—"}</b>
                      <span className="text-[#6B6B61] ml-1 font-mono">
                        ({row.agency_path})
                      </span>
                    </div>
                  </div>
                  <pre className="bg-[#F5F5F0] rounded px-2 py-1 text-[10px] font-mono text-[#1C1C19] whitespace-pre-wrap break-all mb-2 max-h-24 overflow-auto">
                    {row.raw}
                  </pre>
                  <div className="mb-2 text-[11px]">
                    {row.trace.final_entries.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        <span className="text-[#6B6B61]">Final:</span>
                        {row.trace.final_entries.map((e, i) => (
                          <span
                            key={`${e.lpg_id}-${e.dac_code}-${e.from_pass || i}`}
                            className="inline-flex items-center gap-1 bg-[#385C4B] text-white px-2 py-0.5 rounded font-mono text-[10px]"
                          >
                            {e.lpg_id} · DAC {e.dac_code}
                            <span className="opacity-70 text-[9px]">
                              ← {e.from_pass}
                            </span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[#8A6046]">
                        No entries extracted — parser could not find a valid LPG-id / DAC pair.
                      </span>
                    )}
                  </div>
                  <details className="text-[10px]">
                    <summary className="cursor-pointer text-[#6B6B61] hover:text-[#385C4B] select-none">
                      Passes ({row.trace.passes.filter((p) => p.matches.length > 0).length} fired)
                    </summary>
                    <div className="mt-2 space-y-1.5 pl-2 border-l border-[#DCDCD3]">
                      {row.trace.passes.map((p, i) => {
                        const anyMatch = p.matches.length > 0;
                        const anyUsed = p.used.length > 0;
                        return (
                          <div
                            key={p.name || `pass-${i}`}
                            className={
                              anyUsed
                                ? "text-[#385C4B]"
                                : anyMatch
                                ? "text-[#8A6046]"
                                : "text-[#B3B3A8]"
                            }
                          >
                            <div className="font-mono">
                              {p.gated_off && (
                                <span className="text-[9px] mr-1 opacity-60">
                                  [gated-off]
                                </span>
                              )}
                              {p.name}
                            </div>
                            <div className="pl-3 text-[#6B6B61] text-[9px]">
                              {p.description}
                            </div>
                            {anyMatch && (
                              <div className="pl-3 font-mono text-[9px]">
                                matches:{" "}
                                {p.matches
                                  .map((m) => `${m[0]}·${m[1]}`)
                                  .join(", ")}
                                {anyUsed && (
                                  <span className="ml-2 text-[#385C4B]">
                                    (used: {p.used.map((m) => `${m[0]}·${m[1]}`).join(", ")})
                                  </span>
                                )}
                                {anyMatch && !anyUsed && (
                                  <span className="ml-2 text-[#8A6046]">
                                    (ignored — {p.gated_off ? "gated off" : "already picked or duplicate"})
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </details>
                </div>
              ))}
            </div>
          </div>
        )}

        {err && (
          <div className="bg-[#8A6046]/10 border border-[#8A6046]/30 text-[#8A6046] rounded-md px-3 py-2 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {err}
          </div>
        )}

        {reuseNotice && (
          <div
            className="bg-[#385C4B]/10 border border-[#385C4B]/30 text-[#385C4B] rounded-md px-3 py-2 text-sm flex items-start gap-2"
            data-testid="bulk-reuse-notice"
          >
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span className="flex-1">{reuseNotice}</span>
            <button
              onClick={() => setReuseNotice("")}
              className="text-[#385C4B]/60 hover:text-[#385C4B]"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {results && (() => {
          // Iter87: hide the entire Dispatched panel — including any
          // "duplicate-only" rows (0 sent, no delivered) — so the
          // operator only sees agencies where something actually got
          // dispatched or an explicit failure happened. Failed rows
          // (r.ok === false) are always retained so errors surface.
          const visibleResults = (results || []).filter((r) => {
            if (!r.ok) return true;
            const sent = Number(r.sent || 0);
            const delivered = Number(r.marked_delivered || 0);
            return sent > 0 || delivered > 0;
          });
          if (visibleResults.length === 0) return null;
          return (
          <div
            className="bg-[#385C4B]/10 border border-[#385C4B]/30 rounded-md p-4 space-y-1"
            data-testid="bulk-results"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#385C4B]">
                <CheckCircle2 className="w-4 h-4" /> Dispatched
              </div>
              {(() => {
                // Iter82: collect message_ids of every SUCCESSFUL dispatch
                // in this commit so the operator can grab a single Excel
                // (one sheet per agency) with the same rules as the
                // per-message chat download.
                const msgIds = visibleResults
                  .filter((r) => r.ok && r.message_id)
                  .map((r) => r.message_id);
                if (msgIds.length === 0) return null;
                return (
                  <button
                    type="button"
                    data-testid="bulk-download-all-excel"
                    disabled={downloadProgress !== null}
                    onClick={async () => {
                      const total = msgIds.length;
                      toast.info(
                        `${total} Excel files download ho rahe hain. Browser puche to Allow karo.`,
                      );
                      let done = 0;
                      let failed = 0;
                      for (let i = 0; i < msgIds.length; i++) {
                        setDownloadProgress({ i: i + 1, total });
                        try {
                          await downloadOneAgency(msgIds[i]);
                          done += 1;
                        } catch (e) {
                          failed += 1;
                          console.error("bulk download error", e);
                        }
                        // 500 ms gap so the browser doesn't rate-limit /
                        // block multi-downloads, and gives the user a
                        // moment to click "Allow" on the first prompt.
                        if (i < msgIds.length - 1) {
                          await new Promise((r) => setTimeout(r, 500));
                        }
                      }
                      setDownloadProgress(null);
                      if (failed === 0) {
                        toast.success(`${done} Excel files downloaded`);
                      } else {
                        toast.warning(
                          `${done} downloaded, ${failed} failed — check console`,
                        );
                      }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[#385C4B] text-white rounded hover:bg-[#2B4A3B] disabled:opacity-60"
                    title={`Download ${msgIds.length} separate Excel file(s), one per agency (chatbox format)`}
                  >
                    <Download className="w-3.5 h-3.5" />
                    {downloadProgress
                      ? `Downloading ${downloadProgress.i}/${downloadProgress.total}…`
                      : `Download All (${msgIds.length} Excel)`}
                  </button>
                );
              })()}
            </div>
            {(() => {
              // Feb 2026: show human-readable agency names instead of the
              // cryptic `Group 4d02d918…` prefix. We snapshot the lookup
              // at commit time (see `commit()` above) because `preview`
              // gets reset after a successful dispatch — without the
              // snapshot the summary would fall back to UUIDs.
              const nameById = {
                ...(Object.keys(resultsNameMap).length ? resultsNameMap : {}),
              };
              // Merge live preview data too so re-dispatches (without a
              // full page reload) still work.
              (preview?.groups || []).forEach((g) => {
                if (g.group_id && !nameById[g.group_id]) nameById[g.group_id] = g.group_name;
              });
              (preview?.available_groups || []).forEach((g) => {
                if (g.id && !nameById[g.id]) nameById[g.id] = g.name;
              });
              return visibleResults.map((r, i) => {
                const label = nameById[r.group_id] || `Group ${(r.group_id || "").slice(0, 8)}…`;
                return (
                  <div key={r.group_id || `bulk-result-${i}`} className="flex items-center gap-2 text-xs text-[#1C1C19] font-mono pl-6" data-testid={`bulk-result-row-${i}`}>
                    <div className="flex-1 min-w-0">
                      {r.ok ? (
                        <>
                          <b className="font-semibold text-[#385C4B]">{label}</b> ·{" "}
                          <b>{r.sent}</b> sent, {r.resent} auto-resent, {r.duplicates}{" "}
                          duplicates skipped
                          {r.marked_delivered > 0 && (
                            <>
                              {" · "}
                              <b className="text-emerald-700">
                                {r.marked_delivered} marked DELIVERED
                              </b>
                            </>
                          )}
                        </>
                      ) : (
                        <span className="text-[#8A6046]">
                          <b className="font-semibold">{label}</b> failed: {r.error}
                        </span>
                      )}
                    </div>
                    {r.ok && r.message_id && (
                      <button
                        type="button"
                        data-testid={`bulk-result-download-${i}`}
                        onClick={async (ev) => {
                          const btn = ev.currentTarget;
                          btn.setAttribute("data-loading", "1");
                          try {
                            await downloadOneAgency(r.message_id, label);
                          } catch (e) {
                            toast.error(e?.message || "Download failed");
                          } finally {
                            btn.removeAttribute("data-loading");
                          }
                        }}
                        title={`Download ${label} Excel`}
                        className="flex-shrink-0 p-1 rounded border border-[#385C4B]/30 text-[#385C4B] hover:bg-[#385C4B] hover:text-white transition-colors data-[loading=1]:opacity-60"
                      >
                        <Download className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              });
            })()}
            {(aliasesSaved.created.length > 0 || aliasesSaved.refreshed.length > 0) && (
              <div
                className="mt-3 pt-3 border-t border-[#385C4B]/25"
                data-testid="bulk-aliases-saved-notice"
              >
                <div className="flex items-center gap-2 text-xs font-semibold text-[#7A5A1F]">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {aliasesSaved.created.length > 0 && (
                    <span>
                      {aliasesSaved.created.length} new agency{" "}
                      {aliasesSaved.created.length === 1 ? "alias" : "aliases"} saved
                    </span>
                  )}
                  {aliasesSaved.created.length > 0 && aliasesSaved.refreshed.length > 0 && (
                    <span>·</span>
                  )}
                  {aliasesSaved.refreshed.length > 0 && (
                    <span>{aliasesSaved.refreshed.length} re-routed</span>
                  )}
                  <span className="text-[#6B6B61] font-normal">
                    — next paste with these labels auto-matches
                  </span>
                </div>
                <div className="mt-1.5 pl-5 space-y-0.5 text-[10px] font-mono text-[#6B6B61] max-h-40 overflow-y-auto">
                  {[...aliasesSaved.created, ...aliasesSaved.refreshed].map((a, i) => (
                    <div key={i}>&ldquo;{a.raw_label}&rdquo;</div>
                  ))}
                </div>
              </div>
            )}
          </div>
          );
        })()}

        {preview && (
          <>
            {preview.groups.length === 0 && preview.unmatched.length === 0 && (
              <div className="bg-white border border-[#DCDCD3] rounded p-4 text-sm text-[#6B6B61]">
                No LPG codes detected in the pasted text.
              </div>
            )}

            {(preview.groups.length > 0 || preview.unmatched.length > 0) && (() => {
              // Aggregate ALL entries across matched groups + manually-routed unmatched.
              // Shown at the top so admin doesn't have to eyeball & sum per-agency cards
              // when pasting large 500+ row dumps.
              const routedUnmatched = preview.unmatched.filter(isRouted);
              const pendingUnmatched = preview.unmatched.filter(
                (u) => !u.assigned_group_id,
              );
              let totalIds = routedUnmatched.length;
              let fresh = 0;
              let cooling = 0;
              let willFlip = 0;
              for (const g of preview.groups) {
                // Grand Total = every dispatchable row. Complaint IDs
                // (wrong_will_flip) ARE included — they'll dispatch and
                // auto-flip wrong → resent. Cooling duplicates only count
                // when the owner has ticked `Force resend`. Keeps
                //     Grand Total  ==  Dispatch button  ==  Result count
                const dispatchable = (g.entries || []).filter((e) => isDispatchable(g.group_id, e));
                totalIds += dispatchable.length;
                if (g.db_counts && !skippedGroups.has(g.group_id)) {
                  fresh += g.db_counts.fresh || 0;
                  cooling += g.db_counts.cooling_duplicate || 0;
                  willFlip += g.db_counts.wrong_will_flip || 0;
                }
              }
              const totalGroups =
                preview.groups.filter((g) =>
                  (g.entries || []).some((e) => isDispatchable(g.group_id, e))
                ).length + routedUnmatched.length;
              const droppedInPaste = preview.dropped_in_paste?.length || 0;
              return (
                <div
                  data-testid="bulk-preview-grand-total"
                  className="bg-[#385C4B] text-white rounded-md p-4 shadow-sm border border-[#385C4B]"
                >
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-white/60">
                        Grand Total · This Paste
                      </div>
                      <div className="mt-1 text-2xl font-bold font-mono" data-testid="bulk-grand-total-ids">
                        {totalIds} <span className="text-sm font-normal text-white/70">ID{totalIds === 1 ? "" : "s"}</span>
                        <span className="mx-2 text-white/40">·</span>
                        {totalGroups} <span className="text-sm font-normal text-white/70">group{totalGroups === 1 ? "" : "s"}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px] font-mono">
                      {fresh > 0 && (
                        <span className="px-2 py-1 rounded bg-emerald-500/25 border border-emerald-300/40" data-testid="bulk-grand-fresh">
                          🆕 <b>{fresh}</b> fresh
                        </span>
                      )}
                      {willFlip > 0 && (
                        <span
                          className="px-2 py-1 rounded bg-blue-500/25 border border-blue-300/40"
                          data-testid="bulk-grand-flip"
                          title="Ye complaint IDs hain — dispatch honge aur status wrong → resent auto-flip ho jaayega. Complaint ka record aap manually clear karoge."
                        >
                          ↻ <b>{willFlip}</b> auto-resent (complaint)
                        </span>
                      )}
                      {cooling > 0 && (
                        <span className="px-2 py-1 rounded bg-slate-500/30 border border-slate-300/30" data-testid="bulk-grand-cooling">
                          🔁 <b>{cooling}</b> already-sent ({preview.cooling_days}d)
                        </span>
                      )}
                      {droppedInPaste > 0 && (
                        <span className="px-2 py-1 rounded bg-amber-500/25 border border-amber-300/40" data-testid="bulk-grand-dropped">
                          ⚠ <b>{droppedInPaste}</b> in-paste dupes
                        </span>
                      )}
                      {pendingUnmatched.length > 0 && (
                        <span className="px-2 py-1 rounded bg-rose-500/25 border border-rose-300/40" data-testid="bulk-grand-unmatched">
                          ? <b>{pendingUnmatched.length}</b> unmatched
                        </span>
                      )}
                    </div>
                  </div>
                  {(cooling > 0 || droppedInPaste > 0) && (
                    <div className="mt-3 pt-3 border-t border-white/15 flex items-center justify-between flex-wrap gap-2">
                      <div className="text-[11px] font-mono text-white/70">
                        Duplicates highlight ke saath Excel report download karein →
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={copyDuplicateWhatsAppText}
                          data-testid="bulk-copy-duplicate-whatsapp"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono font-semibold uppercase tracking-wider bg-emerald-500 text-white rounded hover:bg-emerald-600 shadow-sm"
                          title="Copy a WhatsApp-friendly plain-text list of every duplicate (with previous date + staff) to clipboard — paste directly into your staff group."
                        >
                          <Copy className="w-3.5 h-3.5" />
                          Copy WhatsApp Text ({cooling + droppedInPaste})
                        </button>
                        <button
                          type="button"
                          onClick={downloadDuplicateReport}
                          data-testid="bulk-download-duplicate-report"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono font-semibold uppercase tracking-wider bg-white text-[#385C4B] rounded hover:bg-white/90 shadow-sm"
                          title="Download an XLSX with every duplicate row highlighted (in-paste = amber, already-sent = red) plus summary counts and previous dispatch date/staff."
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download Duplicate Report ({cooling + droppedInPaste})
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {preview.diagonal_shift_warning?.detected && (
              <div
                className="bg-red-500/10 border-2 border-red-600 rounded-md p-4 space-y-2"
                data-testid="bulk-preview-diagonal-shift"
              >
                <div className="flex items-center gap-2 text-sm font-bold text-red-800">
                  <AlertTriangle className="w-4 h-4" />
                  ⚠ DIAGONAL SHIFT DETECTED — {preview.diagonal_shift_warning.count} of {preview.diagonal_shift_warning.total_rows} rows misaligned
                </div>
                <p className="text-[11px] text-red-800 pl-6 leading-relaxed">
                  Har row ka <b>DAC</b> next row ka <b>LPG</b> ban raha hai — ye portal-export bug ya column
                  misalignment ki wajah se hota hai. In-paste duplicate count is silently inflated. Recommended:
                  <b> source Excel/CSV ko fix karke re-upload karo</b>. Aap chaho toh &quot;Dispatch&quot; dabakar aage
                  bhi bad sakte ho, but ledger galat ban sakta hai.
                </p>
                {preview.diagonal_shift_warning.samples?.length > 0 && (
                  <div className="pl-6 space-y-0.5 font-mono text-[10px] text-red-900">
                    <div className="font-semibold">Sample mismatches:</div>
                    {preview.diagonal_shift_warning.samples.map((s, i) => (
                      <div key={`${s.row1_lpg}-${s.row2_lpg}-${i}`}>
                        Row A: LPG=<b>{s.row1_lpg}</b>, DAC=<b>{s.row1_dac}</b> · Row B: LPG=<b>{s.row2_lpg}</b>
                        {" "}← DAC(A) === LPG(B)
                      </div>
                    ))}
                  </div>
                )}
                <label
                  className="flex items-center gap-2 pl-6 pt-2 border-t border-red-500/30 mt-2 text-[11px] font-mono text-red-900 cursor-pointer select-none"
                  data-testid="bulk-diagonal-confirm-label"
                >
                  <input
                    type="checkbox"
                    checked={diagonalConfirmed}
                    onChange={(e) => setDiagonalConfirmed(e.target.checked)}
                    data-testid="bulk-diagonal-confirm-checkbox"
                    className="w-4 h-4 accent-red-600"
                  />
                  Maine <b>manually check kar liya</b>, data sahi hai — dispatch unlock karo
                </label>
              </div>
            )}

            {preview.dropped_in_paste && preview.dropped_in_paste.length > 0 && (
              <div
                className="bg-amber-500/10 border-2 border-amber-500/60 rounded-md p-4 space-y-2"
                data-testid="bulk-preview-dropped"
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                  <AlertTriangle className="w-4 h-4" />
                  In-paste duplicates ({preview.dropped_in_paste.length}) — same LPG appeared twice in YOUR paste
                </div>
                <p className="text-[11px] text-amber-800/80 pl-6">
                  Ye rows aapke pasted text me hi 2-2 baar the (same agency, same LPG ID).
                  Hum sirf ek bhej rahe hain. Manually verify karne ke liye <b>LPG ID</b> aur <b>DAC</b> highlighted hain — click on any to copy.
                </p>
                <div className="pl-6 max-h-64 overflow-y-auto space-y-1.5">
                  {preview.dropped_in_paste.map((d, i) => (
                    <div
                      key={`${d.lpg_id}-${d.dac_code}-${i}`}
                      className="flex flex-wrap items-center gap-2 py-1.5 px-2 bg-amber-500/5 border border-amber-500/30 rounded"
                      data-testid={`bulk-dup-row-${i}`}
                    >
                      <span className="text-[11px] text-amber-900/80 font-mono min-w-[140px]">
                        {d.agency_label || "(no agency)"}
                      </span>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(d.lpg_id).then(() => toast.success(`LPG ID ${d.lpg_id} copied`))}
                        data-testid={`bulk-dup-lpg-${i}`}
                        title="Click to copy LPG ID"
                        className="px-2 py-0.5 bg-amber-600 text-white text-xs font-mono font-bold rounded hover:bg-amber-700 cursor-pointer"
                      >
                        {d.lpg_id}
                      </button>
                      {d.mobile && (
                        <button
                          type="button"
                          onClick={() => navigator.clipboard?.writeText(d.mobile).then(() => toast.success(`Mobile ${d.mobile} copied`))}
                          data-testid={`bulk-dup-mobile-${i}`}
                          title="Click to copy Mobile"
                          className="px-2 py-0.5 bg-amber-500 text-white text-xs font-mono font-bold rounded hover:bg-amber-600 cursor-pointer"
                        >
                          Mob {d.mobile}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(d.dac_code).then(() => toast.success(`DAC ${d.dac_code} copied`))}
                        data-testid={`bulk-dup-dac-${i}`}
                        title="Click to copy DAC"
                        className="px-2 py-0.5 bg-amber-800 text-white text-xs font-mono font-bold rounded hover:bg-amber-900 cursor-pointer"
                      >
                        DAC {d.dac_code}
                      </button>
                      <span className="text-[10px] text-amber-800/70 italic">
                        — {d.reason}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Prominent "which rows are flipping to auto-resent" card so
                admins can immediately see the LPG IDs going from
                complaint→resent without hunting through matched-agency
                accordions. Same treatment for "already-sent (cooling)"
                below — both surface previous-dispatch metadata inline. */}
            {(() => {
              const flipRows = [];
              const coolRows = [];
              for (const g of preview.groups || []) {
                for (const e of g.entries || []) {
                  if (e.db_status === "wrong_will_flip") {
                    flipRows.push({ ...e, group_name: g.group_name });
                  } else if (e.db_status === "cooling_duplicate") {
                    coolRows.push({ ...e, group_name: g.group_name, group_id: g.group_id });
                  }
                }
              }
              return (
                <>
                  {flipRows.length > 0 && (
                    <div
                      className="bg-blue-500/10 border-2 border-blue-500/60 rounded-md p-4 space-y-2"
                      data-testid="bulk-preview-wrong-flip"
                    >
                      <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                        <AlertTriangle className="w-4 h-4" />
                        Auto-resent ({flipRows.length}) — pehle complaint (wrong) thi, ab dispatch hone par status <b>wrong → resent</b> auto-flip hoga
                      </div>
                      <p className="text-[11px] text-blue-900/80 pl-6">
                        Ye IDs bhi dispatch hongi (fresh ke saath).
                        Complaint clearance aap <b>manually</b> Chat / Complaint Box se karenge — is dispatch me kuch skip nahi hoga.
                      </p>
                      <div className="pl-6 max-h-64 overflow-y-auto space-y-1.5">
                        {flipRows.map((e, i) => (
                          <div
                            key={`${e.lpg_id}-${e.dac_code}`}
                            className="flex flex-wrap items-center gap-2 py-1.5 px-2 bg-blue-500/5 border border-blue-500/30 rounded"
                            data-testid={`bulk-flip-row-${i}`}
                          >
                            <span className="text-[11px] text-blue-900/80 font-mono min-w-[140px]">
                              {e.group_name}
                            </span>
                            <button
                              type="button"
                              onClick={() => navigator.clipboard?.writeText(e.lpg_id).then(() => toast.success(`LPG ID ${e.lpg_id} copied`))}
                              data-testid={`bulk-flip-lpg-${i}`}
                              title="Click to copy LPG ID"
                              className="px-2 py-0.5 bg-blue-600 text-white text-xs font-mono font-bold rounded hover:bg-blue-700 cursor-pointer"
                            >
                              {e.lpg_id}
                            </button>
                            {e.mobile && (
                              <button
                                type="button"
                                onClick={() => navigator.clipboard?.writeText(e.mobile).then(() => toast.success(`Mobile ${e.mobile} copied`))}
                                data-testid={`bulk-flip-mobile-${i}`}
                                title="Click to copy Mobile"
                                className="px-2 py-0.5 bg-blue-500/80 text-white text-xs font-mono font-bold rounded hover:bg-blue-600 cursor-pointer"
                              >
                                Mob {e.mobile}
                              </button>
                            )}
                            <span className="px-2 py-0.5 bg-blue-800/90 text-white text-xs font-mono font-bold rounded">
                              DAC {e.dac_code}
                            </span>
                            {e.last_sent_at && (
                              <span className="text-[10px] text-blue-900/80 italic">
                                — earlier complaint on <b>{formatPrevDispatchDate(e.last_sent_at)}</b>
                                {e.last_sent_uploader && <> by <b>{e.last_sent_uploader}</b></>}
                                {e.last_sent_dac && e.last_sent_dac !== e.dac_code && <> · earlier DAC <b>{e.last_sent_dac}</b></>}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {coolRows.length > 0 && (
                    <CoolingPanel
                      rows={coolRows}
                      coolingDays={preview.cooling_days}
                      lockDays={lockDays}
                      sendAnyway={sendAnyway}
                      onToggle={toggleSendAnyway}
                      canForce={canForce}
                    />
                  )}
                </>
              );
            })()}

            {preview.groups.length > 0 && (
              <div className="space-y-3">
                <MatchedAgenciesHeader
                  groups={preview.groups}
                  skipped={skippedGroups}
                  onRestore={toggleSkipped}
                  onRestoreAll={() => setSkippedGroups(new Set())}
                />
                {preview.groups.filter((g) => !skippedGroups.has(g.group_id)).map((g) => {
                  // Set of LPG IDs that had an in-paste duplicate — used to
                  // highlight the surviving copy so admin can spot both the
                  // dropped and the kept version side by side.
                  const dupLpgSet = new Set(
                    (preview.dropped_in_paste || []).map((d) => d.lpg_id)
                  );
                  return (
                  <div
                    key={g.group_id}
                    data-testid="bulk-matched-group"
                    className="bg-white border border-[#385C4B]/30 rounded-md p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="font-semibold text-[#385C4B]">
                          {g.group_name}
                        </div>
                        <div className="text-[10px] font-mono text-[#6B6B61]">
                          {g.entries.length} entries detected
                        </div>
                        {g.db_counts && (
                          <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`bulk-db-counts-${g.group_id}`}>
                            {g.db_counts.fresh > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-800 font-mono font-semibold">
                                🆕 {g.db_counts.fresh} fresh
                              </span>
                            )}
                            {g.db_counts.cooling_duplicate > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-700 font-mono font-semibold">
                                🔁 {g.db_counts.cooling_duplicate} already-sent ({preview.cooling_days}d)
                              </span>
                            )}
                            {g.db_counts.wrong_will_flip > 0 && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-800 font-mono font-semibold"
                                title="Complaint IDs — dispatch hongi aur wrong → resent auto-flip ho jaayega. Complaint aap manually clear karoge."
                              >
                                ↻ {g.db_counts.wrong_will_flip} auto-resent
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <label
                        className="flex items-center gap-1.5 cursor-pointer select-none text-[10px] font-mono uppercase tracking-wider text-[#385C4B]"
                        title="Untick = is agency ki IDs dispatch nahi hongi"
                      >
                        <input
                          type="checkbox"
                          checked
                          onChange={() => toggleSkipped(g.group_id)}
                          data-testid={`bulk-agency-select-${g.group_id}`}
                          className="w-5 h-5 accent-[#385C4B] cursor-pointer"
                        />
                        Send
                      </label>
                    </div>
                    <div className="max-h-40 overflow-y-auto text-xs font-mono space-y-0.5 mt-2 border-t border-[#DCDCD3] pt-2">
                      {(() => {
                        // Sort so that DUPLICATES surface at the TOP of
                        // each group — otherwise a 200-row paste with 3
                        // duplicates hides them under the fresh entries
                        // and the user (correctly) says "koi duplicate
                        // nahi dikh raha". Fresh entries stay in their
                        // original order below.
                        const rank = (s) =>
                          s === "cooling_duplicate" ? 0 :
                          s === "wrong_will_flip" ? 1 :
                          2;
                        return g.entries
                          .map((e, idx) => ({ e, idx }))
                          .sort((a, b) => {
                            const ra = rank(a.e.db_status);
                            const rb = rank(b.e.db_status);
                            return ra !== rb ? ra - rb : a.idx - b.idx;
                          })
                          .slice(0, 60)
                          .map(({ e, idx: i }) => {
                        const isInPasteDup = dupLpgSet.has(e.lpg_id);
                        // DB status classes — the emerald "fresh" ones are
                        // the actionable rows (will actually dispatch).
                        // Slate = will-be-silently-dropped as duplicate.
                        let dbClass = "text-[#1C1C19]";
                        let dbBadge = null;
                        if (e.db_status === "fresh") {
                          dbClass = "text-emerald-800 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5";
                          dbBadge = <span className="text-[9px] uppercase font-semibold text-emerald-800 tracking-wider">🆕 fresh</span>;
                        } else if (e.db_status === "cooling_duplicate" && isCoolDispatchable(g.group_id, e)) {
                          dbClass = "text-emerald-800 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5";
                          dbBadge = <span className="text-[9px] uppercase font-semibold text-emerald-800 tracking-wider" data-testid="bulk-send-anyway-badge">✔ send anyway</span>;
                        } else if (e.db_status === "cooling_duplicate") {
                          dbClass = "text-slate-600 bg-slate-500/5 border border-slate-500/20 rounded px-1.5 py-0.5 opacity-70";
                          dbBadge = <span className="text-[9px] uppercase font-semibold text-slate-500 tracking-wider">🔁 already-sent</span>;
                        } else if (e.db_status === "wrong_will_flip") {
                          dbClass = "text-blue-800 bg-blue-500/10 border border-blue-500/30 rounded px-1.5 py-0.5";
                          dbBadge = <span className="text-[9px] uppercase font-semibold text-blue-800 tracking-wider" title="Ye complaint ID hai — dispatch hoga, status wrong → resent auto-flip ho jaayega. Complaint aap manually clear karoge.">↻ auto-resent</span>;
                        }
                        if (isInPasteDup) {
                          dbClass = "text-amber-900 bg-amber-500/15 border border-amber-500/40 rounded px-1.5 py-0.5";
                        }
                        // "Previously dispatched on DD-MM-YYYY by <staff>"
                        // hint — shown only when the row is a DB duplicate
                        // (cooling window) or a status-flip candidate so
                        // admins never have to hunt the chat history to
                        // find out WHEN and BY WHOM a row was sent last.
                        const prevDate = formatPrevDispatchDate(e.last_sent_at);
                        const prevUploader = (e.last_sent_uploader || "").trim();
                        const prevDac = (e.last_sent_dac || "").trim();
                        const showPrevHint =
                          prevDate &&
                          (e.db_status === "cooling_duplicate" ||
                            e.db_status === "wrong_will_flip");
                        return (
                          <div
                            key={`${e.lpg_id}-${e.dac_code}-${i}`}
                            className={`${dbClass} flex flex-col gap-0.5`}
                            data-testid={
                              isInPasteDup ? "bulk-matched-dup-entry" :
                              e.db_status === "fresh" ? "bulk-matched-fresh-entry" :
                              e.db_status === "cooling_duplicate" ? "bulk-matched-cooling-entry" : undefined
                            }
                          >
                            <div className="flex items-center gap-2 justify-between">
                              <span>
                                {e.lpg_id}
                                {e.mobile && (
                                  <> · <span data-testid="bulk-matched-mobile" className="text-[#7A5A1F]">Mob {e.mobile}</span></>
                                )}
                                {" "}· DAC {e.dac_code}
                              </span>
                              <span className="flex items-center gap-2 shrink-0">
                                {dbBadge}
                                {isInPasteDup && (
                                  <span
                                    className="text-[9px] font-semibold uppercase tracking-wider text-amber-800"
                                    title="This LPG also appeared as an in-paste duplicate above"
                                  >
                                    🔁 duplicate above
                                  </span>
                                )}
                              </span>
                            </div>
                            {showPrevHint && (
                              <div
                                className="text-[10px] font-mono text-slate-600 italic pl-1"
                                data-testid="bulk-prev-dispatch-hint"
                                title={`Previously sent on ${prevDate}${prevUploader ? ` by ${prevUploader}` : ""}${prevDac ? ` · DAC ${prevDac}` : ""}`}
                              >
                                ↳ previously sent on <b>{prevDate}</b>
                                {prevUploader && (
                                  <> by <b>{prevUploader}</b></>
                                )}
                                {prevDac && prevDac !== e.dac_code && (
                                  <> · earlier DAC <b>{prevDac}</b></>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      });
                      })()}
                      {g.entries.length > 60 && (
                        <div className="text-[#6B6B61]">
                          … {g.entries.length - 60} more
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {preview.unmatched.length > 0 && (
              <div className="bg-white border border-[#8A6046]/40 rounded-md p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-[#8A6046]" />
                  <div className="font-semibold text-[#8A6046]">
                    Unmatched · {preview.unmatched.length}
                  </div>
                </div>
                <p className="text-[11px] text-[#6B6B61] mb-3">
                  Agency name didn't match any group. Pick a group from the
                  dropdown — <b>all rows with the same label auto-assign</b>{" "}
                  and the mapping is <b>saved permanently</b> so next paste
                  matches automatically.
                </p>

                {/* Bulk-assign: single click routes every unassigned row */}
                {preview.unmatched.some((u) => !u.assigned_group_id) && (
                  <div
                    className="flex items-center gap-2 flex-wrap mb-3 p-2 bg-[#385C4B]/5 border border-[#385C4B]/30 rounded"
                    data-testid="bulk-assign-all-bar"
                  >
                    <span className="text-[11px] font-mono uppercase tracking-wider text-[#385C4B]">
                      Assign all remaining ({preview.unmatched.filter((u) => !u.assigned_group_id).length}) →
                    </span>
                    <select
                      value={bulkAssignGid}
                      onChange={(e) => setBulkAssignGid(e.target.value)}
                      className="px-2 py-1 text-xs border border-[#DCDCD3] rounded bg-white flex-1 min-w-[180px]"
                      data-testid="bulk-assign-all-select"
                    >
                      <option value="">— pick group for all unassigned —</option>
                      {[...preview.available_groups]
                        .sort((a, b) =>
                          String(a.name || "").localeCompare(
                            String(b.name || ""),
                            "en",
                            { numeric: true, sensitivity: "base" }
                          )
                        )
                        .map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      onClick={applyBulkAssign}
                      disabled={!bulkAssignGid}
                      data-testid="bulk-assign-all-apply"
                      className="px-3 py-1 text-[11px] font-semibold bg-[#385C4B] text-white rounded hover:bg-[#2D4A3C] disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Apply the picked group to every unmatched row that hasn't been routed yet"
                    >
                      Apply to all
                    </button>
                  </div>
                )}

                {/* Apply-all-suggestions: routes every unassigned row that
                    has a fuzzy suggestion to ITS OWN suggested group.
                    When agencies are mixed, one click handles all of them
                    at once — different agencies land in different buckets. */}
                {(() => {
                  const suggestableCount = preview.unmatched.filter(
                    (u) => !u.assigned_group_id && u.suggested_group_id
                  ).length;
                  if (suggestableCount === 0) return null;
                  return (
                    <div
                      className="flex items-center gap-2 flex-wrap mb-3 p-2 bg-[#F0DFA5]/25 border border-[#DFC578] rounded"
                      data-testid="bulk-apply-suggestions-bar"
                    >
                      <span className="text-[11px] font-mono uppercase tracking-wider text-[#7A5A1F]">
                        {suggestableCount} row{suggestableCount === 1 ? "" : "s"} have fuzzy suggestions
                      </span>
                      <button
                        type="button"
                        onClick={applyAllSuggestions}
                        data-testid="bulk-apply-all-suggestions"
                        className="ml-auto px-3 py-1 text-[11px] font-semibold bg-[#7A5A1F] text-white rounded hover:bg-[#5C441A]"
                        title="Route every unassigned row to its own suggested group — different agencies go to different buckets automatically"
                      >
                        ↪ Apply all suggestions
                      </button>
                    </div>
                  );
                })()}

                {/* Per-agency mini-panels — grouped so users can Apply All
                    for a single agency without touching other agency rows.
                    Order: rows with a suggestion first (they auto-solve),
                    then rows without a suggestion. Within each bucket the
                    rows keep the same individual dropdown / suggest / new
                    / discard controls as before. */}
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {(() => {
                    // Build agency buckets from the current unmatched list.
                    // Keep the ORIGINAL row index so `setUnmatchedGroup(i, …)`
                    // still targets the right entry after grouping.
                    const buckets = new Map();
                    preview.unmatched.forEach((u, origIdx) => {
                      const key = (u.agency_label || "(no agency)").trim();
                      let b = buckets.get(key);
                      if (!b) {
                        b = {
                          label: key,
                          rows: [],
                          suggested_group_id: u.suggested_group_id || "",
                          suggested_group_name: u.suggested_group_name || "",
                          suggested_score: u.suggested_score || 0,
                        };
                        buckets.set(key, b);
                      }
                      b.rows.push({ u, i: origIdx });
                    });
                    // Sort: suggestion-carrying buckets first, then by size desc
                    const orderedBuckets = Array.from(buckets.values()).sort((a, b) => {
                      const aHas = a.suggested_group_id ? 1 : 0;
                      const bHas = b.suggested_group_id ? 1 : 0;
                      if (aHas !== bHas) return bHas - aHas;
                      return b.rows.length - a.rows.length;
                    });
                    return orderedBuckets.map((bucket) => {
                      const pendingInBucket = bucket.rows.filter((r) => !r.u.assigned_group_id).length;
                      return (
                        <div
                          key={bucket.label}
                          className={`border rounded-md ${
                            bucket.suggested_group_id
                              ? "bg-[#FEF7E0] border-[#F0DFA5]"
                              : "bg-white border-[#DCDCD3]"
                          }`}
                          data-testid="bulk-agency-bucket"
                        >
                          <div className="flex items-center gap-2 flex-wrap px-3 py-2 border-b border-[#DCDCD3]/60">
                            <span className="text-xs font-semibold text-[#1C1C19]">
                              &ldquo;{bucket.label}&rdquo;
                            </span>
                            <span className="text-[10px] font-mono text-[#6B6B61]">
                              · {bucket.rows.length} row{bucket.rows.length === 1 ? "" : "s"}
                              {pendingInBucket !== bucket.rows.length && (
                                <> · {bucket.rows.length - pendingInBucket} assigned</>
                              )}
                            </span>
                            {bucket.suggested_group_id && pendingInBucket > 0 && (
                              <button
                                type="button"
                                onClick={() =>
                                  applyAllForAgency(bucket.label, bucket.suggested_group_id)
                                }
                                className="ml-auto px-2.5 py-1 text-[10px] font-semibold bg-[#7A5A1F] text-white rounded hover:bg-[#5C441A]"
                                data-testid="bulk-apply-agency-suggestion"
                                title={`Fuzzy match score ${Math.round((bucket.suggested_score || 0) * 100)}% — assigns all ${pendingInBucket} pending row(s) of this agency to ${bucket.suggested_group_name}`}
                              >
                                ↪ Apply all {pendingInBucket} → {bucket.suggested_group_name}
                              </button>
                            )}
                            {!bucket.suggested_group_id && pendingInBucket > 0 && (
                              <div
                                className="ml-auto flex items-center gap-1"
                                data-testid="bulk-agency-manual-pick"
                              >
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      applyAllForAgency(bucket.label, e.target.value);
                                      e.target.value = "";
                                    }
                                  }}
                                  defaultValue=""
                                  className="px-2 py-1 text-[10px] border border-[#DCDCD3] rounded bg-white"
                                  data-testid="bulk-agency-manual-select"
                                  title={`Manually pick a group and assign all ${pendingInBucket} row(s) of this agency`}
                                >
                                  <option value="">— pick + apply to all {pendingInBucket} —</option>
                                  {[...preview.available_groups]
                                    .sort((a, b) =>
                                      String(a.name || "").localeCompare(
                                        String(b.name || ""),
                                        "en",
                                        { numeric: true, sensitivity: "base" }
                                      )
                                    )
                                    .map((g) => (
                                      <option key={g.id} value={g.id}>
                                        {g.name}
                                      </option>
                                    ))}
                                </select>
                              </div>
                            )}
                          </div>
                          <div className="p-2 space-y-1">
                            {bucket.rows.map(({ u, i }) => (
                              <div
                                key={`${u.lpg_id}-${u.dac_code}-${i}`}
                                className={`flex items-center gap-2 text-xs font-mono ${
                                  u.db_status === "cooling_duplicate" ? "opacity-60" : ""
                                }`}
                                data-testid="bulk-unmatched-row"
                              >
                                <span className={`flex-shrink-0 ${u.db_status === "cooling_duplicate" ? "text-red-700 line-through" : "text-[#6B6B61]"}`}>
                                  {u.lpg_id}
                                  {u.mobile && (
                                    <> · <span data-testid="bulk-unmatched-mobile" className="text-[#7A5A1F] no-underline">Mob {u.mobile}</span></>
                                  )}
                                  {" "}· DAC {u.dac_code}
                                </span>
                                {u.db_status === "cooling_duplicate" && (
                                  <span
                                    className="px-1.5 py-0.5 text-[9px] font-mono font-bold bg-red-100 text-red-800 border border-red-300 rounded flex-shrink-0"
                                    data-testid="bulk-unmatched-dup-badge"
                                    title={`Already sent to "${u.last_sent_agency || "?"}" by ${u.last_sent_uploader || "?"} · DAC ${u.last_sent_dac || "?"} · ${(u.last_sent_at || "").slice(0, 10)}. Assign karoge to ye row bhi dispatch hogi (fresh).`}
                                  >
                                    ALREADY SENT · {(u.last_sent_agency || "?").slice(0, 18)} · {(u.last_sent_at || "").slice(0, 10)}
                                  </span>
                                )}
                                {u.suggested_group_id && !u.assigned_group_id && (
                                  <button
                                    onClick={() =>
                                      setUnmatchedGroup(i, u.suggested_group_id)
                                    }
                                    className="px-2 py-0.5 text-[10px] font-semibold bg-[#F0DFA5] text-[#7A5A1F] border border-[#DFC578] rounded hover:bg-[#F5E7B8] flex-shrink-0"
                                    data-testid="bulk-suggest-btn"
                                    title={`Fuzzy match score ${Math.round((u.suggested_score || 0) * 100)}% — click to assign this row + all others with the same label`}
                                  >
                                    ↪ {u.suggested_group_name}
                                  </button>
                                )}
                                <select
                                  value={u.assigned_group_id}
                                  onChange={(e) => setUnmatchedGroup(i, e.target.value)}
                                  className={`ml-auto px-2 py-1 text-xs border rounded bg-white ${
                                    u.assigned_group_id
                                      ? "border-[#385C4B]"
                                      : u.suggested_group_id
                                        ? "border-[#DFC578]"
                                        : "border-[#DCDCD3]"
                                  }`}
                                  data-testid="bulk-assign-select"
                                >
                                  <option value="">— assign group —</option>
                                  {[...preview.available_groups]
                                    .sort((a, b) =>
                                      String(a.name || "").localeCompare(
                                        String(b.name || ""),
                                        "en",
                                        { numeric: true, sensitivity: "base" }
                                      )
                                    )
                                    .map((g) => (
                                      <option key={g.id} value={g.id}>
                                        {g.name}
                                        {g.id === u.suggested_group_id ? "  ← suggested" : ""}
                                      </option>
                                    ))}
                                </select>
                                <button
                                  onClick={() =>
                                    setCreateAgency({
                                      agency_label: u.agency_label,
                                      name: u.agency_label || "",
                                    })
                                  }
                                  className="px-2 py-1 text-[10px] font-semibold bg-[#385C4B] text-white rounded hover:bg-[#2D4A3C] flex items-center gap-1 flex-shrink-0"
                                  data-testid="bulk-create-agency-button"
                                  title="Create new agency (auto-add user + group with password Patna@123)"
                                >
                                  <Plus className="w-3 h-3" /> New
                                </button>
                                <button
                                  onClick={() => removeUnmatched(i)}
                                  className="text-[#6B6B61] hover:text-[#8A6046] p-1"
                                  title="Discard this row"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {(preview.groups.length > 0 || preview.unmatched.length > 0) && (
              <div className="bg-white border border-[#DCDCD3] rounded-md p-4 sticky bottom-4 shadow-md">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4 flex-wrap">
                    <label
                      className="flex items-center gap-2 text-xs text-[#385C4B] cursor-pointer font-medium"
                      title="Ticked — sabhi rows ko dispatch ke saath hi DELIVERED (green) mark kar do. Portal me DELIVERED column na ho tab bhi kaam karega."
                    >
                      <input
                        type="checkbox"
                        checked={markDelivered}
                        onChange={(e) => setMarkDelivered(e.target.checked)}
                        data-testid="bulk-mark-delivered-checkbox"
                      />
                      <span>
                        Mark all as <b>DELIVERED</b>
                        <span className="text-[#6B6B61] font-normal ml-1">
                          (portal me DELIVERED tag na ho toh)
                        </span>
                      </span>
                    </label>
                    {user?.role === "admin" && <DispatchDatePicker value={dispatchDate} onChange={setDispatchDate} />}
                  </div>
                  <button
                    onClick={commit}
                    disabled={
                      committing ||
                      // Diagonal-shift guard — block dispatch until admin
                      // manually confirms the paste is actually correct.
                      (preview.diagonal_shift_warning?.detected && !diagonalConfirmed)
                    }
                    data-testid="bulk-commit-button"
                    className="px-5 py-2 bg-[#385C4B] text-white text-sm rounded hover:bg-[#2D4A3C] disabled:opacity-50 flex items-center gap-2"
                    title={
                      preview.diagonal_shift_warning?.detected && !diagonalConfirmed
                        ? "Diagonal shift detected — pehle upar wala checkbox tick karo (manually verify karke)"
                        : ""
                    }
                  >
                    {committing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    {(() => {
                      const { totalIds, totalGroups } = dispatchTotals();
                      return (
                        <span data-testid="bulk-dispatch-label">
                          Dispatch <b>{totalIds}</b> ID{totalIds === 1 ? "" : "s"} to{" "}
                          {totalGroups} group{totalGroups === 1 ? "" : "s"}
                          {backdated && <> · {shortDateLabel(dispatchDate)}</>}
                        </span>
                      );
                    })()}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Create New Agency modal */}
      {createAgency && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4"
          onClick={() => !createBusy && setCreateAgency(null)}
        >
          <div
            className="bg-white rounded-lg p-5 w-full max-w-md border border-[#DCDCD3] shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="bulk-create-agency-modal"
          >
            <div className="flex items-center justify-between mb-3">
              <h2
                className="text-lg font-semibold text-[#1C1C19]"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Create New Agency
              </h2>
              <button
                onClick={() => setCreateAgency(null)}
                className="text-[#6B6B61] hover:text-[#1C1C19]"
                disabled={createBusy}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-[#6B6B61] mb-3">
              A new <b>user account</b> + <b>group</b> will be created with
              password <span className="font-mono bg-[#F5F5F0] px-1 rounded">Patna@123</span>.
              The pasted label{" "}
              {createAgency.agency_label && (
                <span className="font-mono italic">
                  "{createAgency.agency_label}"
                </span>
              )}{" "}
              will be saved as an alias so future pastes auto-match.
            </p>
            <p className="text-[11px] text-[#8A6046] mb-3 leading-relaxed">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              <b>Duplicate guard:</b> if a similar agency already exists, the
              system will reuse it instead of creating a copy.
            </p>
            <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">
              Official agency name
            </label>
            <input
              autoFocus
              value={createAgency.name}
              onChange={(e) =>
                setCreateAgency({ ...createAgency, name: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") submitCreateAgency();
                if (e.key === "Escape") setCreateAgency(null);
              }}
              maxLength={80}
              data-testid="bulk-create-agency-name-input"
              placeholder="e.g. CHANDI INDANE GAS SERVICE"
              className="w-full mt-1 px-3 py-2 border border-[#DCDCD3] rounded text-sm focus:outline-none focus:border-[#385C4B]"
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => setCreateAgency(null)}
                disabled={createBusy}
                className="px-3 py-1.5 text-sm text-[#6B6B61] hover:bg-[#F5F5F0] rounded"
              >
                Cancel
              </button>
              <button
                onClick={submitCreateAgency}
                disabled={createBusy || !createAgency.name.trim()}
                data-testid="bulk-create-agency-submit"
                className="px-4 py-1.5 bg-[#385C4B] text-white text-sm rounded hover:bg-[#2D4A3C] disabled:opacity-50 flex items-center gap-2"
              >
                {createBusy ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Plus className="w-3.5 h-3.5" />
                )}
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {deliveredFile && (
        <DeliveredExcelModal
          file={deliveredFile}
          uploaderName={uploaderName}
          onClose={() => setDeliveredFile(null)}
          onSuccess={() => {
            /* keep modal open on success so user can read the summary,
               dismiss via Close button */
          }}
        />
      )}

      {/* Iter97: Manage dispatchers popover — admin-only. Toggle any
          team member on/off; disabled ones instantly disappear from
          the dropdown. */}
      {showManageModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setShowManageModal(false)}
          data-testid="bulk-uploader-manage-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-[#DCDCD3] flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-[#1C1C19]">
                  Manage dispatchers
                </h2>
                <p className="text-[11px] font-mono text-[#6B6B61] mt-0.5">
                  {manageMembers.filter((m) => m.enabled).length} enabled ·{" "}
                  {manageMembers.filter((m) => !m.enabled).length} disabled
                </p>
              </div>
              <button
                onClick={() => setShowManageModal(false)}
                data-testid="bulk-uploader-manage-close"
                className="p-1 hover:bg-[#F7F6EE] rounded"
                title="Close"
              >
                <X className="w-4 h-4 text-[#6B6B61]" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {manageLoading ? (
                <div className="py-12 text-center text-xs font-mono text-[#6B6B61]">
                  Loading…
                </div>
              ) : manageMembers.length === 0 ? (
                <div className="py-12 text-center text-xs font-mono text-[#6B6B61]">
                  Koi team member nahi mila. Admin → Users me role=team_staff
                  wala user create karo.
                </div>
              ) : (
                <ul className="divide-y divide-[#EFEEE6]">
                  {manageMembers.map((m) => (
                    <li
                      key={m.id}
                      className="px-5 py-3 flex items-center gap-3"
                      data-testid={`bulk-uploader-manage-row-${m.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-sm font-semibold truncate ${
                            m.enabled ? "text-[#1C1C19]" : "text-[#9A9A91] line-through"
                          }`}
                        >
                          {m.staff_name}
                        </div>
                        <div className="text-[10px] font-mono text-[#6B6B61] truncate">
                          Registered · ledger-linked
                          {m.display_name && m.display_name !== m.staff_name
                            ? ` · ${m.display_name}`
                            : ""}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleMemberEnabled(m)}
                        disabled={manageTogglingId === m.id}
                        data-testid={`bulk-uploader-manage-toggle-${m.id}`}
                        className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-40 ${
                          m.enabled ? "bg-[#385C4B]" : "bg-[#DCDCD3]"
                        }`}
                        title={m.enabled ? "Click to disable" : "Click to enable"}
                      >
                        <span
                          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${
                            m.enabled ? "left-5" : "left-0.5"
                          }`}
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-5 py-3 border-t border-[#DCDCD3] bg-[#F7F6EE] text-[10px] font-mono text-[#6B6B61] text-center">
              Setting auto-save. Disabled members dropdown me nahi dikhenge
              par unke historical uploads intact rahenge.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
